import { ReactNode } from "react";
import { Table } from "./components/Table";
import { TableHeader } from "./components/TableHeader";
import { TableBody } from "./components/TableBody";
import { TableRow } from "./components/TableRow";
import { TableCell } from "./components/TableCell";
import { Column } from "./types";

const SkeletonRow = ({ columns }: { columns: Column<any>[] }) => (
  <TableRow className="animate-pulse">
    {columns.map((col) => (
      <TableCell key={col.key} align={col.align} className={col.cellClassName}>
        <div className="h-4 bg-gray-200 rounded" />
      </TableCell>
    ))}
  </TableRow>
);

function DataRow<T>({
  row,
  columns,
  onRowClick,
  renderExpandedRow,
  className,
}: {
  row: T;
  columns: Column<T>[];
  onRowClick?: (row: T) => void;
  renderExpandedRow?: (row: T) => ReactNode | null;
  className?: string;
}) {
  const expandedContent = renderExpandedRow?.(row);

  return (
    <>
      <TableRow
        className={className}
        onClick={onRowClick ? () => onRowClick(row) : undefined}
        // Only expandable tables get the attribute; a plain row must not claim to expand
        ariaExpanded={renderExpandedRow ? expandedContent != null : undefined}
      >
        {columns.map((col) => (
          <TableCell key={col.key} align={col.align} className={col.cellClassName}>
            {col.cell(row)}
          </TableCell>
        ))}
      </TableRow>
      {expandedContent != null ? (
        <tr className="!border-t-0">
          <td colSpan={columns.length} className="p-0">{expandedContent}</td>
        </tr>
      ) : null}
    </>
  );
}

type DataTableProps<T> = {
  columns: Column<T>[];
  rows: T[];
  getRowKey: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  isLoading?: boolean;
  skeletonRowCount?: number;
  className?: string;
  /** Full-width content rendered in an extra row below `row`; return null when collapsed. */
  renderExpandedRow?: (row: T) => ReactNode | null;
  /** Per-row class on the <tr> — e.g. dim hidden rows. */
  rowClassName?: (row: T) => string | undefined;
};

/**
 * Config-driven table: pass `columns` + `rows` and it renders the shell, header,
 * and body. Owns only in-table concerns (rows, empty, loading skeleton). Error
 * and pagination stay with the consuming page/wrapper. For irregular tables,
 * compose the building blocks in ./components directly instead.
 */
export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  onRowClick,
  empty,
  isLoading,
  skeletonRowCount = 5,
  className, // className for table
  renderExpandedRow,
  rowClassName,
}: DataTableProps<T>) {
  return (
    <Table className={className}>
      <TableHeader columns={columns} />
      <TableBody colSpan={columns.length} isEmpty={!isLoading && rows.length === 0} empty={empty}>
        {isLoading
          ? Array.from({ length: skeletonRowCount }).map((_, i) => <SkeletonRow key={`skeleton-${i}`} columns={columns} />)
          : rows.map((row, index) => (
            <DataRow
              key={getRowKey(row, index)}
              row={row}
              columns={columns}
              onRowClick={onRowClick}
              renderExpandedRow={renderExpandedRow}
              className={rowClassName?.(row)}
            />
          ))}
      </TableBody>
    </Table>
  );
}

export default DataTable;
